import { useState, useMemo } from 'react';
import type { DetectionResult, MetricField, SprintInfo, ColumnMapping, ParsedMetricRow } from '../../utils/sprintImport';
import { ALL_METRIC_FIELDS, METRIC_LABELS, parseNumber } from '../../utils/sprintImport';

interface DetectionPreviewProps {
  result: DetectionResult;
  sprints: SprintInfo[];
  existingMetricsCount: number;
  onImport: (result: DetectionResult) => void;
  onBulkEdit: (result: DetectionResult) => void;
  onDiscard: () => void;
  onSwapOrientation: () => void;
}

const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: '_sprintName', label: 'Sprint Name' },
  ...ALL_METRIC_FIELDS.map((f) => ({ value: f, label: METRIC_LABELS[f] })),
  { value: '_ignore', label: '-- Ignore column' },
];

export default function DetectionPreview({
  result,
  sprints,
  existingMetricsCount,
  onImport,
  onBulkEdit,
  onDiscard,
  onSwapOrientation,
}: DetectionPreviewProps) {
  const [columns, setColumns] = useState<ColumnMapping[]>(result.columns);
  const [sprintMatches, setSprintMatches] = useState(() =>
    result.rows.map((r) => r.sprintMatch)
  );

  // Re-parse metric values whenever columns change, using rawData
  const effectiveRows = useMemo<ParsedMetricRow[]>(() => {
    const metricCols = columns.filter(
      (c) => c.field !== null && c.field !== '_sprintName'
    );

    return result.rawData.map((raw, i) => {
      const values: Partial<Record<MetricField, number | null>> = {};
      for (const col of metricCols) {
        const rawVal = raw[col.sourceIndex] || '';
        values[col.field as MetricField] = parseNumber(rawVal, result.locale);
      }
      return {
        sprintMatch: sprintMatches[i] || {
          rowIndex: i,
          sourceValue: '',
          sprint: null,
          confidence: 0,
        },
        values,
      };
    });
  }, [result.rawData, result.locale, columns, sprintMatches]);

  const matchedCount = effectiveRows.filter((r) => r.sprintMatch.sprint).length;
  const unmatchedCount = effectiveRows.filter(
    (r) => r.sprintMatch.sourceValue && !r.sprintMatch.sprint
  ).length;

  const handleColumnChange = (index: number, value: string) => {
    setColumns((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        field: value === '_ignore' ? null : (value as MetricField | '_sprintName'),
        confidence: 1.0,
      };
      return next;
    });
  };

  const handleSprintChange = (rowIndex: number, sprintId: string) => {
    setSprintMatches((prev) => {
      const next = [...prev];
      const sprint = sprintId === '_skip'
        ? null
        : sprints.find((s) => s.sprintId === sprintId) || null;
      next[rowIndex] = {
        ...next[rowIndex],
        sprint: sprint ? { ...sprint } : null,
        confidence: sprint ? 1.0 : 0,
      };
      return next;
    });
  };

  const currentResult: DetectionResult = {
    ...result,
    columns,
    rows: effectiveRows,
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Detected {matchedCount} sprint{matchedCount !== 1 ? 's' : ''} with metrics data
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span>Orientation: {result.orientation === 'normal' ? 'Rows = Sprints' : 'Rows = Metrics'}</span>
          <button
            onClick={onSwapOrientation}
            className="text-accent-blue hover:underline text-xs font-medium"
          >
            Swap
          </button>
        </div>

        {existingMetricsCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {existingMetricsCount} sprint{existingMetricsCount !== 1 ? 's' : ''} already have metrics — importing will overwrite
          </div>
        )}

        {result.warnings.map((w, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {w}
          </div>
        ))}
      </div>

      {/* Column Mapping */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Column Mapping</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-500">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Your Column</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Maps To</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Sample</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col, i) => {
                // Show raw cell value from first data row — always accurate regardless of mapping
                const rawRow = result.rawData[0];
                const sampleValue = rawRow ? (rawRow[col.sourceIndex] || '').trim() : '';
                return (
                  <tr key={i} className="border-b border-gray-100 dark:border-dark-600">
                    <td className="py-2 px-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                      &quot;{col.sourceHeader}&quot;
                    </td>
                    <td className="py-2 px-3">
                      <select
                        className="input text-xs py-1"
                        value={col.field || '_ignore'}
                        onChange={(e) => handleColumnChange(i, e.target.value)}
                      >
                        {FIELD_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500 dark:text-gray-400">
                      {sampleValue || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sprint Matching */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Sprint Matching</h4>
        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white dark:bg-dark-700">
              <tr className="border-b border-gray-200 dark:border-dark-500">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Source</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Matched Sprint</th>
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {effectiveRows.map((row, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-dark-600">
                  <td className="py-2 px-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                    &quot;{row.sprintMatch.sourceValue}&quot;
                  </td>
                  <td className="py-2 px-3">
                    <select
                      className="input text-xs py-1"
                      value={row.sprintMatch.sprint?.sprintId || '_skip'}
                      onChange={(e) => handleSprintChange(i, e.target.value)}
                    >
                      <option value="_skip">-- Skip row</option>
                      {sprints.map((s) => (
                        <option key={s.sprintId} value={s.sprintId}>{s.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    {row.sprintMatch.sprint ? (
                      <span className="text-green-600 dark:text-green-400 text-xs font-medium">Match</span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">None</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {unmatchedCount > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            {unmatchedCount} sprint{unmatchedCount !== 1 ? 's' : ''} not matched. Use the dropdown to assign manually or leave as &quot;Skip&quot;.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-500">
        <button onClick={onDiscard} className="btn-outline text-sm">
          Discard
        </button>
        <button onClick={() => onBulkEdit(currentResult)} className="btn-outline text-sm">
          Bulk Edit
        </button>
        <button
          onClick={() => onImport(currentResult)}
          className="btn-primary text-sm"
          disabled={matchedCount === 0}
        >
          Import ({matchedCount} sprint{matchedCount !== 1 ? 's' : ''})
        </button>
      </div>
    </div>
  );
}
